<script setup lang="ts">
/**
 * A row of quick-reply chips (#1738): the answers a conversation offers for the question it is on, each emitting
 * its `value` (never its label) when chosen. With `allowOther`, a "Something else" chip opens a text input and a
 * Send button that emits the trimmed text; blank text is never sent.
 *
 * Sending does not clear the typed text: the parent may still refuse the answer (a save the server turns down), and
 * a refused answer must not be lost. The parent owns it with `v-model:draft` and clears it once the answer is taken,
 * or moves on to the next question by remounting the component with a new `key`. Unbound, the text stays until then.
 *
 * Built from `ZhButton` and `ZhInput`, so every chip is a real `<button>` in the tab order and the input keeps its
 * label for a screen reader. `disabled` disables every control, so nothing is emitted while the answer is saving.
 */
import { ref, useId } from 'vue'
import ZhButton from './ZhButton.vue'
import ZhInput from './ZhInput.vue'

export interface QuickReply {
    label: string
    value: string
}

const props = withDefaults(
    defineProps<{
        replies: QuickReply[]
        /** Offer a "Something else" chip that reveals a text input with a send button. */
        allowOther?: boolean
        disabled?: boolean
        /** The label of the free-text chip. */
        otherLabel?: string
        /** The input's label, read by a screen reader. */
        inputLabel?: string
    }>(),
    {
        allowOther: false,
        disabled: false,
        otherLabel: 'Something else',
        inputLabel: 'Your answer',
    },
)

const emit = defineEmits<{
    /** The value of the chip chosen. */
    select: [value: string]
    /** The trimmed text typed under "Something else". */
    text: [text: string]
}>()

/** The text typed under "Something else". Kept after it is sent, until the parent clears it. */
const typed = defineModel<string>('draft', { default: '' })

const inputId = `zh-quick-replies-${useId()}`
const typing = ref(false)

function choose(value: string): void {
    if (!props.disabled) {
        emit('select', value)
    }
}

function openOther(): void {
    if (!props.disabled) {
        typing.value = true
    }
}

function send(): void {
    const text = typed.value.trim()
    if (props.disabled || text === '') {
        return
    }
    emit('text', text)
}
</script>

<template>
    <div class="zh-quick-replies">
        <div class="zh-quick-replies__chips" role="group">
            <ZhButton
                v-for="reply in replies"
                :key="reply.value"
                variant="ghost"
                class="zh-quick-replies__chip"
                :disabled="disabled"
                @click="choose(reply.value)"
            >
                {{ reply.label }}
            </ZhButton>
            <ZhButton
                v-if="allowOther"
                variant="ghost"
                class="zh-quick-replies__chip"
                :disabled="disabled"
                :aria-expanded="typing ? 'true' : 'false'"
                @click="openOther"
            >
                {{ otherLabel }}
            </ZhButton>
        </div>
        <div v-if="allowOther && typing" class="zh-quick-replies__other">
            <ZhInput :id="inputId" v-model="typed" :label="inputLabel" @keydown.enter.prevent="send" />
            <ZhButton class="zh-quick-replies__send" :disabled="disabled" @click="send">Send</ZhButton>
        </div>
    </div>
</template>

<style scoped>
.zh-quick-replies {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.zh-quick-replies__chips {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
}

.zh-quick-replies__chip {
    min-height: 2.5rem;
    padding: 0 1rem;
    text-transform: none;
    letter-spacing: var(--tracking-system);
}

.zh-quick-replies__other {
    display: flex;
    align-items: flex-end;
    gap: 0.5rem;
}
</style>
